import React, { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import {
  XMarkIcon,
  ArrowPathIcon,
  BuildingLibraryIcon,
  ExclamationCircleIcon,
  BanknotesIcon,
} from "@heroicons/react/24/outline";
import { CreateCommanWay } from "API/commonApi";
import UseToast from "utils/AfToast";
import { GenericColumnData } from "utils/TableColumnData";

interface BankBalanceModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const BankBalanceModal: React.FC<BankBalanceModalProps> = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [balanceData, setBalanceData] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);

  const fetchBalances = async () => {
    setLoading(true);
    setError(null);
    try {
      const res: any = await CreateCommanWay({
        url: "/admin/payment/fetch-balance",
        data: {},
      });

      if (res?.data && Array.isArray(res.data)) {
        setBalanceData(res.data);
      } else if (res?.data?.data && Array.isArray(res.data.data)) {
        setBalanceData(res.data.data);
      } else if (Array.isArray(res)) {
        setBalanceData(res);
      } else {
        setBalanceData([]);
        if (res?.message) {
          setError(res.message);
        }
      }
    } catch (err: any) {
      console.error("Error fetching bank balances:", err);
      const errMsg = err?.response?.data?.message || err?.message || "Failed to fetch bank balances";
      setError(errMsg);
      UseToast(errMsg, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchBalances();
    }
  }, [isOpen]);

  const formatCurrency = (val: any) => {
    const num = parseFloat(val);
    if (isNaN(num)) return "₹ 0.00";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(num);
  };

  const getAccountLocation = (item: any) => {
    const key = String(item?.debitAccountKey || item?.key || "");
    const debitAccOptions =
      GenericColumnData?.user?.tableColumnData?.find(
        (d: any) => d.key === "personalData.debitAcc"
      )?.options || [];

    const foundOption = debitAccOptions.find(
      (opt: any) => String(opt.value) === key
    );

    if (foundOption?.label) {
      return foundOption.label;
    }
    return item?.location || item?.branchName || (key ? `Branch #${key}` : "Main Account");
  };

  const getAccountBalance = (item: any) => {
    if (item?.status === "FAILED") return null;
    const resp = item?.response;
    if (!resp) return item?.balance ?? null;
    return (
      resp?.FetchAccountDetailsResponse?.balance ??
      resp?.availableBalance ??
      resp?.effectiveAvailableBalance ??
      resp?.balance ??
      null
    );
  };

  // Calculate total balance across successful accounts
  const totalBalance = balanceData.reduce((acc, item) => {
    const bal = getAccountBalance(item);
    if (bal !== null && !isNaN(parseFloat(bal))) {
      return acc + parseFloat(bal);
    }
    return acc;
  }, 0);

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[1050]" onClose={onClose}>
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-hidden">
          <div className="flex min-h-full items-center justify-center p-3 text-center sm:p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95 translate-y-4"
              enterTo="opacity-100 scale-100 translate-y-0"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100 translate-y-0"
              leaveTo="opacity-0 scale-95 translate-y-4"
            >
              <Dialog.Panel className="w-full max-w-2xl h-[90vh] flex flex-col transform overflow-hidden rounded-2xl bg-white p-4 text-left align-middle shadow-2xl transition-all border border-gray-100">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-gray-100">
                  <div className="flex items-center gap-2.5">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <BuildingLibraryIcon className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <Dialog.Title as="h3" className="text-base font-bold text-gray-900 font-quicksand">
                        Bank Account Balances
                      </Dialog.Title>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={fetchBalances}
                      disabled={loading}
                      title="Refresh Balances"
                      className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors disabled:opacity-50"
                    >
                      <ArrowPathIcon className={`h-5 w-5 ${loading ? "animate-spin text-primary" : ""}`} />
                    </button>
                    <button
                      type="button"
                      onClick={onClose}
                      className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                {/* Body Content */}
                <div className="mt-3 flex-1 flex flex-col min-h-0 overflow-hidden">
                  {loading ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-center">
                      <div className="relative flex items-center justify-center">
                        <div className="h-14 w-14 rounded-full border-4 border-primary/20 border-t-primary animate-spin" />
                        <BanknotesIcon className="h-6 w-6 text-primary absolute" />
                      </div>
                      <h4 className="mt-3 text-sm font-semibold text-gray-800">Fetching Bank Balances...</h4>
                    </div>
                  ) : error ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-center py-6">
                      <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-red-100 text-red-600 mb-2">
                        <ExclamationCircleIcon className="h-5 w-5" />
                      </div>
                      <h4 className="text-sm font-semibold text-gray-900">Failed to Fetch Balances</h4>
                      <p className="text-xs text-red-500 mt-1 max-w-md mx-auto">{error}</p>
                      <button
                        type="button"
                        onClick={fetchBalances}
                        className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary/90 transition-colors shadow-sm"
                      >
                        <ArrowPathIcon className="h-3.5 w-3.5" /> Try Again
                      </button>
                    </div>
                  ) : (
                    <div className="flex-1 flex flex-col min-h-0 overflow-hidden font-inter">
                      {/* Aggregate Summary Bar (Side by Side) */}
                      {balanceData.length > 0 && (
                        <div className="mb-3 bg-gradient-to-r from-slate-900 via-slate-800 to-primary/90 text-white px-3.5 py-1 rounded-xl shadow-md flex items-center justify-between shrink-0">
                          <span className="text-xs font-semibold uppercase tracking-wider text-slate-200">
                            Total Available Balance:
                          </span>
                          <span className="text-xl font-bold font-quicksand">
                            {formatCurrency(totalBalance)}
                          </span>
                        </div>
                      )}

                      {/* Accounts List (Flex-1 Auto Height Scrollable) */}
                      {balanceData.length === 0 ? (
                        <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">
                          No account balance data returned.
                        </div>
                      ) : (
                        <div className="space-y-2 flex-1 overflow-y-auto pr-1 min-h-0">
                          {balanceData.map((item: any, idx: number) => {
                            const isFailed = item?.status === "FAILED";
                            const location = getAccountLocation(item);
                            const balance = getAccountBalance(item);
                            const accNo = item?.accountNumber || item?.response?.accountNo || "-";

                            return (
                              <div
                                key={idx}
                                className={`px-3 py-1 rounded-xl border transition-all ${
                                  isFailed
                                    ? "border-red-200 bg-red-50/50"
                                    : "border-gray-200 bg-white hover:border-primary/40 hover:shadow-sm"
                                }`}
                              >
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <span className="font-semibold text-sm text-gray-900">
                                        {location}
                                      </span>
                                      {isFailed && (
                                        <span className="inline-flex items-center gap-1 rounded-md bg-red-100 px-2 py-0.5 text-[11px] font-medium text-red-700">
                                          <ExclamationCircleIcon className="h-3 w-3" /> Failed
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-3 text-[11px] text-gray-400 mt-0.5">
                                      <span>A/C: <strong className="text-gray-700 font-mono">{accNo}</strong></span>
                                      {item?.bicCode && (
                                        <span>BIC: <strong className="text-gray-700 font-mono">{item.bicCode}</strong></span>
                                      )}
                                    </div>
                                  </div>

                                  <div className="text-right">
                                    <span className="text-[11px] font-medium text-gray-400 uppercase tracking-wider block">
                                      Balance
                                    </span>
                                    {isFailed ? (
                                      <span className="text-xs text-red-600 font-medium">
                                        {typeof item?.error === "string"
                                          ? item.error
                                          : item?.error?.message || "Unavailable"}
                                      </span>
                                    ) : (
                                      <span className="text-base font-bold text-emerald-600 font-quicksand block">
                                        {formatCurrency(balance)}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default BankBalanceModal;
